/** "Everything you need to know" accordion-style FAQ. */

import { cn } from '@rocco/utils/cn';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';

const FAQ_ITEMS = [
    {
        question: 'Is Home Design Evening free?',
        answer: "Yes. Registration is completely free. You'll also get a free ArchiPro account which gives you access to mood boards, product research, professional connections, and more.",
    },
    {
        question: 'Who is this event for?',
        answer: `Anyone planning a building project — whether you're renovating a bathroom or building a new home. It's also valuable for architects, designers, and builders looking to connect with brands and peers.`,
    },
    {
        question: 'Can I bring a guest?',
        answer: `Yes. During registration you can add one guest. They'll receive their own digital ticket and can create their own ArchiPro account.`,
    },
    {
        question: 'What should I bring?',
        answer: 'Just your phone. Your digital ticket, event scanner, and mood board are all in your ArchiPro profile. We recommend downloading the ArchiPro app before the evening.',
    },
    {
        question: 'Is there a dresscode?',
        answer: 'Smart casual. The evening is relaxed but polished — think of it as a design-forward networking event.',
    },
    {
        question: 'What is ArchiPro?',
        answer: `ArchiPro is New Zealand and Australia's leading curated platform for architecture, building, and design. Over 400,000 people use ArchiPro to research products, discover professionals, and plan building projects. Every business on the platform is vetted.`,
    },
    {
        question: "I'm not currently building, should I still come?",
        answer: `Absolutely. Many attendees are in the early inspiration phase. Home Design Evening is designed to help you understand what's possible, meet the people who make it happen, and start planning with confidence.`,
    },
    {
        question: "Will the panel be useful if I'm just starting out?",
        answer: `Especially so. The panel is designed to educate homeowners at every stage — from choosing your first architect to understanding budgets and consents. It's the conversation you wish you could have before your build begins.`,
    },
    {
        question: 'What happens after the event?',
        answer: `Everything you scan and save at the event lives in your ArchiPro account permanently. You can research products, compare options, and enquire with professionals at your own pace. ArchiPro becomes your building companion.`,
    },
];

/** Single icon morph for plus/minus transitions. */
const ToggleIcon = ({ expanded }: { expanded: boolean }) => (
    <svg
        width="21"
        height="21"
        viewBox="0 0 21 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="0" y="9.5" width="21" height="2" fill="#141414" />
        <rect
            x="9.5"
            y="0"
            width="2"
            height="21"
            fill="#141414"
            style={{
                transformOrigin: '10.5px 10.5px',
                transform: expanded ? 'scaleY(0)' : 'scaleY(1)',
                transition: 'transform 200ms ease-out',
            }}
        />
    </svg>
);

export const FAQ = ({ isMobile = false }: { isMobile?: boolean }) => {
    const [expandedItem, setExpandedItem] = useState('0');
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.2,
    });

    if (isMobile) {
        return (
            <section
                ref={sectionRef}
                data-testid="faq"
                className="relative w-full bg-white pt-[2rem] pb-0"
            >
                <h2 className="max-w-[14.125rem] px-[1.5rem] font-ghost text-[1.625rem] leading-[0.96] tracking-[-0.02em] text-[#141414]">
                    Everything you need to know
                </h2>
                <Accordion
                    type="single"
                    collapsible
                    value={expandedItem}
                    onValueChange={(value) => setExpandedItem(value)}
                    className="mt-[1.1875rem]"
                >
                    {FAQ_ITEMS.map((item, index) => {
                        const itemValue = String(index);
                        const isExpanded = expandedItem === itemValue;

                        return (
                            <AccordionItem
                                key={index}
                                value={itemValue}
                                variant="noBorder"
                                className="border-t border-[#dbdbdb]"
                            >
                                <AccordionTrigger className="w-full px-[1.5rem] py-[1.0625rem] text-left [&>svg]:hidden">
                                    <div className="flex w-full items-center justify-between">
                                        <span className="pr-[0.75rem] text-[0.9375rem] leading-[1.08] font-normal tracking-[-0.01em] text-[#141414]">
                                            {item.question}
                                        </span>
                                        <span className="relative block h-[1rem] w-[1rem] text-[1rem] leading-none text-[#9a9a9a]">
                                            <span
                                                className={cn(
                                                    'absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out',
                                                    isExpanded
                                                        ? 'opacity-0'
                                                        : 'opacity-100',
                                                )}
                                            >
                                                +
                                            </span>
                                            <span
                                                className={cn(
                                                    'absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out',
                                                    isExpanded
                                                        ? 'opacity-100'
                                                        : 'opacity-0',
                                                )}
                                            >
                                                −
                                            </span>
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="overflow-hidden !transition-none data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down [&>div]:p-0 [&>div]:transition-opacity [&>div]:duration-300 [&>div]:ease-out data-[state=closed]:[&>div]:opacity-0 data-[state=open]:[&>div]:opacity-100">
                                    <p
                                        className={cn(
                                            'px-[1.5rem] pb-[1.125rem] font-ghost text-[0.9375rem] leading-[1.33] tracking-[-0.01em] text-[#141414] transition-opacity duration-300 ease-out',
                                            isExpanded
                                                ? 'opacity-100 delay-300'
                                                : 'opacity-0 delay-0',
                                        )}
                                    >
                                        {item.answer}
                                    </p>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                    <div className="border-t border-[#dbdbdb]" />
                </Accordion>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="faq"
            className="relative w-full bg-white pt-[10rem] pb-[10rem]"
        >
            {/* Title */}
            <h2
                className={cn(
                    'pl-[3.5rem] font-ghost text-[4.8125rem] leading-[1] tracking-[-0.096rem] text-black transition-all duration-600 ease-out',
                    inView
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-6 opacity-0',
                )}
            >
                Everything you need to know
            </h2>

            {/* FAQ list — full width with px-[3.5rem] matching Figma (56px each side) */}
            <div className="mt-13 mb-7 px-[3.5rem]">
                <Accordion
                    type="single"
                    collapsible
                    value={expandedItem}
                    onValueChange={(value) => setExpandedItem(value)}
                >
                    {FAQ_ITEMS.map((item, index) => {
                        const itemValue = String(index);
                        const isExpanded = expandedItem === itemValue;

                        return (
                            <AccordionItem
                                key={index}
                                value={itemValue}
                                variant="noBorder"
                                className="border-t border-[#141414]/40"
                            >
                                <AccordionTrigger className="box-content w-full py-5 text-left [&>svg]:hidden">
                                    <div className="flex w-full items-center justify-between">
                                        <span className="font-sans text-[1.75rem] leading-[normal] font-medium tracking-[-0.0525rem] text-[#141414]">
                                            {item.question}
                                        </span>
                                        <span className="mr-4 flex shrink-0 items-center justify-center">
                                            <ToggleIcon expanded={isExpanded} />
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="overflow-hidden !transition-none data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down [&>div]:p-0">
                                    <div
                                        className={cn(
                                            'pb-14 pl-[44rem] transition-opacity duration-300 ease-out',
                                            isExpanded
                                                ? 'opacity-100 delay-500'
                                                : 'opacity-0 delay-0',
                                        )}
                                    >
                                        <p className="w-[56.25rem] font-ghost text-[2.75rem] leading-[1] tracking-[-0.065rem] text-black">
                                            {item.answer}
                                        </p>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
                {/* Bottom border for last item */}
                <div className="border-t border-[#141414]/40" />
            </div>
        </section>
    );
};
